import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/web/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/web/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/web/components/ui/select";
import { Input } from "@/web/components/ui/input";
import { Button } from "@/web/components/atoms/button";
import type { CategoryMapping } from "@/types";

// Zod schema for category mapping form validation
const categoryMappingFormSchema = z.object({
  storeId: z.string().min(1, "Store is required"),
  storeCategoryId: z.string().min(1, "Store category ID is required"),
});

type CategoryMappingFormData = z.infer<typeof categoryMappingFormSchema>;

export interface CategoryMappingFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  shoppyCategoryId: string;
  onSubmit: (data: CategoryMappingFormData & { shoppyCategoryId: string }) => void;
  isLoading?: boolean;
}

export function CategoryMappingFormModal({
  isOpen,
  onOpenChange,
  shoppyCategoryId,
  onSubmit,
  isLoading = false,
}: CategoryMappingFormModalProps) {
  const form = useForm<CategoryMappingFormData>({
    resolver: zodResolver(categoryMappingFormSchema),
    defaultValues: {
      storeId: "",
      storeCategoryId: "",
    },
  });

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      form.reset({
        storeId: "",
        storeCategoryId: "",
      });
    }
  }, [isOpen, form]);

  const handleSubmit = (data: CategoryMappingFormData) => {
    onSubmit({ ...data, shoppyCategoryId });
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  const storeOptions = ["Coles", "Aldi", "Woolworths"];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Category Mapping</DialogTitle>
          <DialogDescription>
            Add a new mapping for Shoppy category ID: {shoppyCategoryId}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="storeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Store</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isLoading}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a store" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {storeOptions.map((store) => (
                        <SelectItem key={store} value={store}>
                          {store}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="storeCategoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Store Category ID</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter store category ID"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Adding..." : "Add Mapping"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
